import { useMemo, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'

import { Alert, Button, Dialog, Form, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  RESET_TRANSACTION_SYNC_CURSOR_KEY,
  resetTransactionSyncCursor,
} from '@/actions/admin/adminFinancialAccountActions'
import { AdminPlaidItem } from '@/reducers/finances/plaidItemReducer'
import { getFieldNames, makeReqStringSchema } from '@/base'
import { getFetchError, getIsFetching } from '@/reducers/fetch'

interface Props {
  onClose: () => void
  plaidItem: AdminPlaidItem
}

export const ResetTransactionSyncDialog = ({ onClose, plaidItem }: Props) => {
  const dispatch = useAppDispatch()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  const PLAID_MAX_HISTORY_DAYS = 730
  const minSyncDate = DateTime.now()
    .minus({ days: PLAID_MAX_HISTORY_DAYS })
    .startOf('day')
    .toJSDate()

  const [confirming, setConfirming] = useState(false)
  const error = useReselector(getFetchError, RESET_TRANSACTION_SYNC_CURSOR_KEY)
  const submitting = useReselector(
    getIsFetching,
    RESET_TRANSACTION_SYNC_CURSOR_KEY
  )

  const formik = useFormik({
    initialValues: {
      syncFromDate: `01-01-${currentTaxYear}`,
      reason: '',
    },
    enableReinitialize: true,
    onSubmit: () => {
      setConfirming(true)
    },
  })

  const { values, submitForm } = formik
  const fieldNames = getFieldNames(formik)

  const handleConfirm = async () => {
    setConfirming(true)
    const res = await dispatch(
      resetTransactionSyncCursor(plaidItem.id, plaidItem.userId, {
        syncFromDate:
          DateTime.fromFormat(values.syncFromDate, DATE_FORMATS_LUXON.INPUT, {
            zone: 'utc',
          }).toISODate() || '',
        reason: values.reason.trim(),
      })
    )

    setConfirming(false)
    if (res) {
      onClose()
    }
  }

  const accountSummary = useMemo(
    () =>
      (plaidItem.accounts ?? [])
        .map((account) => `${account.name} (•••${account.mask})`)
        .join(', ') || 'No linked accounts',
    [plaidItem.accounts]
  )

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content size="md">
        <Dialog.Header>
          <Dialog.Title>Reset Transaction Sync</Dialog.Title>
          <Dialog.SubTitle>
            {plaidItem.institutionName} · Plaid Item ID {plaidItem.itemId}
          </Dialog.SubTitle>
        </Dialog.Header>

        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Text variant={TypographyStyles.BODY_SM}>
                <b>Linked accounts:</b> {accountSummary}
              </Text>
            </div>

            {confirming ? (
              <div className="flex flex-col gap-3">
                <Alert variant="warning">
                  Confirm the cursor reset and re-sync for this Plaid item.
                </Alert>
                <Text variant={TypographyStyles.BODY_SM}>
                  <b>Institution:</b> {plaidItem.institutionName}
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  <b>Plaid Item ID:</b> {plaidItem.itemId}
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  <b>Pull transactions from:</b> {values.syncFromDate}
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  <b>Reason:</b> {values.reason}
                </Text>
              </div>
            ) : (
              <>
                <Alert variant="warning">
                  <b>This will trigger a full re-sync of transactions.</b>{' '}
                  Existing transactions are de-duplicated by Plaid transaction
                  ID, but new transactions added since the last sync will be
                  pulled in. The sync may take a few minutes to complete.
                </Alert>

                <Form.DateInput
                  name={fieldNames.syncFromDate}
                  label="Pull transactions from"
                  schema={makeReqStringSchema()}
                  minDate={minSyncDate}
                />
                <Text variant={TypographyStyles.BODY_SM}>
                  Plaid limits transaction history to 730 days.
                </Text>

                <Form.Textarea
                  name={fieldNames.reason}
                  label="Reason"
                  schema={makeReqStringSchema()}
                  placeholder="e.g., Late joiner needs 2025 transactions backfilled"
                  rows={3}
                />
              </>
            )}

            {error && (
              <Alert variant="error">
                <Text variant={TypographyStyles.BODY_SM}>{error.message}</Text>
              </Alert>
            )}
          </div>
        </FormikProvider>

        <Dialog.Footer>
          {!confirming ? (
            <>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={submitForm}>Continue</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Back
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirm}
                disabled={submitting}
                loading={submitting}
              >
                Confirm Reset
              </Button>
            </>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
